import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem as MenuItemMUI,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  Chip,
  Snackbar,
  Alert,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { Visibility, CheckCircle, DoNotDisturbOn, ReportProblem, InfoOutlined, MoreVert, ContentCopy, Add, UploadFile, PictureAsPdf, Delete } from '@mui/icons-material';
import driveSvg from '../../assets/images/google-drive.svg';
import { listMaterialApprovals, quickCreateKzmUpload, getKzmDetails, type MaterialApprovalDto, type MaterialApprovalBranch, reviseKzm, deleteKzm, sendKzmEmail } from '../../services/materialApprovalsService';
import { API_BASE_URL } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listUsersForBusinessForms, type AppUser } from '../../services/userService';
import KzmSettings from './KzmSettings';
import { getKzmSettings, type KzmSettingsDto } from '../../services/kzmSettingsService';

interface KzmTabProps {
  projectName: string;
  projectData?: { project_id: string; project_name?: string; [key: string]: any };
}

const branches: MaterialApprovalBranch[] = ['Budowlana','Sanitarna','Elektryczna','Teletechniczna'];
const CARD_STATUSES = ['Przygotowana','Sprawdzona','Wysłana','Do poprawienia'];
const APPROVAL_STATUSES = ['Zatwierdzone bez uwag','Zatwierdzone z uwagami','Odrzucone'];

const KzmTab: React.FC<KzmTabProps> = ({ projectData }) => {
  const projectId = String(projectData?.project_id || '');
  const [settingsOpen, setSettingsOpen] = React.useState<boolean>(false);
  const [statusTab, setStatusTab] = React.useState<string>('Wszystkie');

  const { user } = useAuth();

  // Dane
  const [rows, setRows] = React.useState<MaterialApprovalDto[]>([]);
  const [users, setUsers] = React.useState<string[]>([]);
  const [usersMap, setUsersMap] = React.useState<Record<string, AppUser>>({});

  const navigate = useNavigate();
  // Menu i podgląd
  const [preview, setPreview] = React.useState<MaterialApprovalDto | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = React.useState<string | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [menuRow, setMenuRow] = React.useState<MaterialApprovalDto | null>(null);

  // Dodawanie/edycja KZM
  const [addOpen, setAddOpen] = React.useState(false);
  const [addBranch, setAddBranch] = React.useState<'' | MaterialApprovalBranch>('');
  const [addMaterialName, setAddMaterialName] = React.useState('');
  const [addInstallPlace, setAddInstallPlace] = React.useState('');
  const [addSubmissionDate, setAddSubmissionDate] = React.useState<string>(() => new Date().toISOString().slice(0, 10));
  const [addCompliance, setAddCompliance] = React.useState<'Zgodny z projektem'|'Równoważny'|'Nie określony w projekcie'|''>('');
  const [attachments, setAttachments] = React.useState<Array<{ id: string; name: string; url: string; driveId?: string; fileObject?: File }>>([]);
  const [saving, setSaving] = React.useState(false);
  const [activeAttachmentId, setActiveAttachmentId] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [snack, setSnack] = React.useState<{ open: boolean; msg: string; severity: 'info'|'success'|'error' }>(()=>({ open: false, msg: '', severity: 'info' }));
  const showSnack = (severity: 'info'|'success'|'error', msg: string) => setSnack({ open: true, severity, msg });

  // Wysyłka KZM – UI-only (lokalne okno)
  const [sendOpen, setSendOpen] = React.useState(false);
  const [sendFrom, setSendFrom] = React.useState<string>('powiadomienia@unitresco.com');
  const [sendTo, setSendTo] = React.useState<string>('');
  const [sendCc, setSendCc] = React.useState<string>('');
  const [sendSubject, setSendSubject] = React.useState<string>('');
  const [sendBody, setSendBody] = React.useState<string>('');
  const [sendAttachments, setSendAttachments] = React.useState<Array<{ id: string; name: string; description?: string; driveId?: string; sizeBytes?: number }>>([]);
  const [sendIncludeAll, setSendIncludeAll] = React.useState<boolean>(true);
  const [sendAttachmentsSizeMB, setSendAttachmentsSizeMB] = React.useState<number>(0);
  const [sendLoading, setSendLoading] = React.useState<boolean>(false);
  const [sendBranch, setSendBranch] = React.useState<string>('');

  // Prefill edycji z menuRow; przy nowym KZM ustaw datę zgłoszenia na dziś
  React.useEffect(() => {
    if (!addOpen) return;
    if (menuRow) {
      (async () => {
        try {
          const det = await getKzmDetails(menuRow.kzm_id || String(menuRow.id));
          const k = det?.kzm || {};
          setAddBranch(((k.material_branch || menuRow.branch) as any) || '');
          setAddMaterialName(k.material_trade_name || menuRow.material_name || '');
          setAddInstallPlace(k.install_place || '');
          const subDate = k.submission_date ? String(k.submission_date).slice(0, 10) : new Date().toISOString().slice(0, 10);
          setAddSubmissionDate(subDate);
          setAddCompliance((k.compliance || '') as any);
          const atts = Array.isArray(det?.attachments) ? det.attachments : [];
          const mapped = atts.map((a:any, idx:number)=>({ id: a.kzm_attachment_id || `Z${idx+1}`, name: a.file_name || `Z${idx+1}.pdf`, url: a.google_drive_file_url || '', driveId: a.google_drive_file_id || '', fileObject: undefined }));
          setAttachments(mapped);
          if (mapped.length) setActiveAttachmentId(mapped[0].id);
        } catch {
          setAddBranch((menuRow.branch as any) || '');
          setAddMaterialName(menuRow.material_name || '');
        }
      })();
    } else {
      setAddSubmissionDate(new Date().toISOString().slice(0, 10));
    }
  }, [addOpen, menuRow]);

  React.useEffect(() => {
    if (!projectId) return;
    listMaterialApprovals(projectId).then((d) => setRows(d || [])).catch(() => setRows([]));
    listUsersForBusinessForms().then((list) => {
      setUsers(Array.from(new Set(list.map(u => u.display_name || '').filter(Boolean))) as string[]);
      const map: Record<string, AppUser> = {}; list.forEach(u => { map[u.user_id] = u; }); setUsersMap(map);
    }).catch(()=>{});
  }, [projectId]);

  // Nasłuchuj zmian decyzji z innej karty
  React.useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'kzm_decision_updated' && projectId) {
        listMaterialApprovals(projectId).then((d) => setRows(d || [])).catch(()=>{});
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [projectId]);

  // Liczenie rozmiaru załączników (DB/HEAD)
  const recomputeAttachmentsSize = React.useCallback(async (atts: Array<{ driveId?: string; sizeBytes?: number }>) => {
    let total = 0;
    for (const a of atts) {
      if (a.sizeBytes && Number.isFinite(a.sizeBytes)) { total += a.sizeBytes as number; continue; }
      if (!a.driveId) continue;
      try {
        const url = `${API_BASE_URL}/kzms/attachments/${encodeURIComponent(a.driveId)}/content`;
        const res = await fetch(url, { method: 'HEAD' as any });
        const len = parseInt(res.headers.get('Content-Length') || res.headers.get('content-length') || '0', 10);
        if (Number.isFinite(len)) total += len;
      } catch {}
    }
    setSendAttachmentsSizeMB(Math.max(0, Math.round((total / (1024*1024)) * 10) / 10));
  }, []);

  // Prefill dialogu wysyłki po wyborze wiersza
  React.useEffect(() => {
    (async () => {
      if (!(sendOpen && menuRow)) return;
      try {
        const det = await getKzmDetails(menuRow.kzm_id || String(menuRow.id));
        const atts = Array.isArray(det?.attachments) ? det.attachments : [];
        const mapped = atts.map((a: any, idx: number) => ({ id: a.kzm_attachment_id || `Z${idx+1}`, name: a.file_name || `Z${idx+1}.pdf`, description: a.file_description || '', driveId: a.google_drive_file_id || '', sizeBytes: a.file_size || undefined }));
        setSendAttachments(mapped);
        recomputeAttachmentsSize(mapped);
        const k = det?.kzm || {};
        setSendBranch(k.material_branch || menuRow.branch || '');
        const folderUrl = k.drive_folder_id ? `https://drive.google.com/drive/folders/${k.drive_folder_id}` : (menuRow as any).google_drive_file_url || '';
        setSendSubject(`KZM ${menuRow.kzm_id || menuRow.id} — ${menuRow.material_name || ''}`.trim());
        setSendBody(['Szanowni Państwo,','',`Przesyłam do akceptacji kartę materiałową ${menuRow.kzm_id || menuRow.id} — ${menuRow.material_name || ''}.`,'','Dokumenty i załączniki znajdują się w folderze Google Drive:',folderUrl || '(brak linku do folderu)','', 'W razie pytań pozostaję do dyspozycji.'].join('\n'));
        // Prefill DO/DW z kzm_settings
        try {
          const projectIdForSettings = k.project_id || projectData?.project_id || '';
          if (projectIdForSettings) {
            const settings: KzmSettingsDto | null = await getKzmSettings(projectIdForSettings);
            if (settings) {
              const sanitize = (s?: string | null) => String(s || '').split(',').map(x => x.trim()).filter(Boolean);
              const uniq = (arr: string[]) => Array.from(new Set(arr));
              const join = (arr: string[]) => uniq(arr).join(', ');
              let toList: string[] = [];
              let ccList: string[] = [];
              const op = sanitize(settings.ordering_party_email);
              const bld = sanitize(settings.inspector_build_email);
              const san = sanitize(settings.inspector_sanitary_email);
              const el  = sanitize(settings.inspector_electrical_email);
              const tel = sanitize(settings.inspector_telecom_email);
              const ccB = sanitize(settings.cc_build);
              const ccS = sanitize(settings.cc_sanitary);
              const ccE = sanitize(settings.cc_electrical);
              const ccT = sanitize(settings.cc_telecom);
              const br = (k.material_branch || menuRow.branch || '') as string;
              switch (br) {
                case 'Budowlana': toList = [...op, ...bld]; ccList = [...tel, ...san, ...el, ...ccB]; break;
                case 'Sanitarna': toList = [...op, ...san]; ccList = [...bld, ...tel, ...el, ...ccS]; break;
                case 'Elektryczna': toList = [...op, ...el]; ccList = [...bld, ...tel, ...san, ...ccE]; break;
                case 'Teletechniczna': toList = [...op, ...tel]; ccList = [...bld, ...san, ...el, ...ccT]; break;
                default: toList = [...op]; ccList = [...bld, ...san, ...el, ...tel];
              }
              setSendTo(join(toList));
              setSendCc(join(ccList));
            }
          }
        } catch {}
      } catch {
        setSendAttachments([]);
        setSendAttachmentsSizeMB(0);
      }
    })();
  }, [sendOpen, menuRow, projectData, recomputeAttachmentsSize]);

  const branchChipColor = (b?: string): 'default'|'primary'|'secondary'|'success'|'warning'|'error'|'info' => {
    switch (String(b || '')) {
      case 'Budowlana': return 'primary';
      case 'Sanitarna': return 'success';
      case 'Elektryczna': return 'warning';
      case 'Teletechniczna': return 'secondary';
      default: return 'default';
    }
  };

  // Ustal URL PDF dla podglądu
  React.useEffect(() => {
    (async () => {
      if (!preview) { setPreviewPdfUrl(null); return; }
      const url = (preview as any)?.generated_kzm_pdf_url || '';
      const toProxy = (u: string) => {
        try {
          const m1 = String(u).match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
          const m2 = String(u).match(/id=([a-zA-Z0-9_-]+)/);
          const m3 = String(u).match(/\/d\/([a-zA-Z0-9_-]+)/);
          const fileId = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]) || null;
          return fileId ? `${API_BASE_URL}/kzms/attachments/${encodeURIComponent(fileId)}/content` : (u || null);
        } catch { return u || null; }
      };
      if (url) { setPreviewPdfUrl(toProxy(url)); return; }
      try {
        const det: any = await getKzmDetails(preview.kzm_id || String(preview.id));
        const pdf = det?.kzm?.generated_kzm_pdf_url || '';
        if (pdf) { setPreviewPdfUrl(toProxy(pdf)); return; }
        const gdoc = det?.kzm?.generated_kzm_google_doc_id || '';
        if (gdoc) setPreviewPdfUrl(toProxy(`https://docs.google.com/document/d/${gdoc}/preview`));
      } catch { setPreviewPdfUrl(null); }
    })();
  }, [preview]);

  const filtered = React.useMemo(() => {
    if (statusTab === 'Wszystkie') return rows;
    if (statusTab === 'Zgłoszone') {
      return rows.filter(r => {
        const cardStatus = String(r.card_status || r.status || '').toLowerCase();
        return cardStatus === 'wysłana' || cardStatus === 'wysłano do akceptacji';
      });
    }
    if (statusTab === 'Zaakceptowane') {
      return rows.filter(r => {
        const approvalStatus = String(r.approval_status || '').toLowerCase();
        return approvalStatus === 'zatwierdzone bez uwag' || approvalStatus === 'zatwierdzone z uwagami';
      });
    }
    if (statusTab === 'Uzupełnić') {
      return rows.filter(r => {
        const cardStatus = String(r.card_status || r.status || '').toLowerCase();
        return cardStatus === 'do poprawienia' || cardStatus === 'wymaga korekty';
      });
    }
    if (statusTab === 'Odrzucone') {
      return rows.filter(r => {
        const approvalStatus = String(r.approval_status || '').toLowerCase();
        return approvalStatus === 'odrzucone';
      });
    }
    return rows;
  }, [rows, statusTab]);

  // Liczniki dla zakładek
  const tabCounts = React.useMemo(() => {
    const all = rows.length;
    const zgłoszone = rows.filter(r => {
      const cardStatus = String(r.card_status || r.status || '').toLowerCase();
      return cardStatus === 'wysłana' || cardStatus === 'wysłano do akceptacji';
    }).length;
    const zaakceptowane = rows.filter(r => {
      const approvalStatus = String(r.approval_status || '').toLowerCase();
      return approvalStatus === 'zatwierdzone bez uwag' || approvalStatus === 'zatwierdzone z uwagami';
    }).length;
    const uzupełnić = rows.filter(r => {
      const cardStatus = String(r.card_status || r.status || '').toLowerCase();
      return cardStatus === 'do poprawienia' || cardStatus === 'wymaga korekty';
    }).length;
    const odrzucone = rows.filter(r => {
      const approvalStatus = String(r.approval_status || '').toLowerCase();
      return approvalStatus === 'odrzucone';
    }).length;
    return { all, zgłoszone, zaakceptowane, uzupełnić, odrzucone };
  }, [rows]);

  // Ikony i chipy
  const approvalStatusIcon = (s?: string) => {
    const title = `Status zatwierdzenia: ${s || '—'}`;
    if (s === 'Zatwierdzone bez uwag') return <Tooltip title={title} arrow><CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /></Tooltip>;
    if (s === 'Zatwierdzone z uwagami') return <Tooltip title={title} arrow><ReportProblem sx={{ fontSize: 18, color: '#FFA000' }} /></Tooltip>;
    if (s === 'Odrzucone') return <Tooltip title={title} arrow><DoNotDisturbOn sx={{ fontSize: 18, color: 'error.main' }} /></Tooltip>;
    return <Tooltip title={title} arrow><InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} /></Tooltip>;
  };
  const cardStatusChip = (s?: string) => {
    const label = s || '—';
    switch (label) {
      case 'Przygotowany':
        return <Chip label={label} size="small" color="info" />;
      case 'Gotowy do wysłania':
        return <Chip label={label} size="small" color="primary" />;
      case 'Wysłano do akceptacji':
        return (
          <Tooltip title="Wysłano do akceptacji" arrow>
            <Chip label="Wysłano" size="small" sx={{ bgcolor: '#FBC02D', color: '#fff' }} />
          </Tooltip>
        );
      case 'Wymaga korekty':
        return <Chip label={label} size="small" color="warning" />;
      case 'Zatwierdzono':
        return <Chip label={label} size="small" color="success" />;
      case 'Zatwierdzono z uwagami':
        return <Chip label={label} size="small" color="warning" variant="outlined" />;
      case 'Odrzucono':
        return <Chip label={label} size="small" color="error" />;
      case 'Anulowano':
        return <Chip label={label} size="small" variant="outlined" />;
      case 'Przygotowana':
        return <Chip label={label} size="small" color="info" />;
      case 'Sprawdzona':
        return <Chip label={label} size="small" color="warning" />;
      case 'Wysłana':
        return <Chip label={label} size="small" color="primary" />;
      case 'Do poprawienia':
        return <Chip label={label} size="small" color="error" />;
      default:
        return <Chip label={label} size="small" variant="outlined" />;
    }
  };

  const renderMenu = () => (
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
      open={Boolean(menuPosition)}
      keepMounted
      onClose={() => { setMenuPosition(null); setMenuRow(null); }}
    >
      <MenuItemMUI onClick={() => { if (menuRow) setSendOpen(true); setMenuPosition(null); }}>Wyślij KZM</MenuItemMUI>
      <MenuItemMUI onClick={() => { if (menuRow) setAddOpen(true); setMenuPosition(null); }}>{'Edytuj'}</MenuItemMUI>
      <MenuItemMUI onClick={() => { if (menuRow) { const kid = encodeURIComponent(menuRow.kzm_id || String(menuRow.id)); const q = (menuRow as any)?.generated_kzm_pdf_url ? `?pdf=${encodeURIComponent((menuRow as any).generated_kzm_pdf_url)}` : ''; window.open(`/app/materials_approvals/${kid}${q}`, '_blank'); } setMenuPosition(null); setMenuRow(null); }}>Okno zatwierdzenia</MenuItemMUI>
    </Menu>
  );

  const handleAddFiles = (files: File[]) => {
    const next: Array<{ id: string; name: string; url: string; fileObject?: File }> = [];
    for (const f of files) {
      try {
        const url = URL.createObjectURL(f);
        next.push({ id: `${Date.now()}-${f.name}-${Math.random()}`, name: f.name, url, fileObject: f });
      } catch {}
    }
    setAttachments(prev => {
      const merged = [...prev, ...next];
      if (!activeAttachmentId && merged.length) setActiveAttachmentId(merged[0].id);
      return merged;
    });
  };

  const activeAttachment = React.useMemo(() => attachments.find(a => a.id === activeAttachmentId) || null, [attachments, activeAttachmentId]);

  React.useEffect(() => {
    if (!addOpen) return;
    return () => { try { attachments.forEach(a => { try { URL.revokeObjectURL(a.url); } catch {} }); } catch {} };
  }, [addOpen]);

  return (
    <Box>
      <Box sx={{ p: 0 }}>
        {/* Zakładki statusów */}
        <Box sx={{ mb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mb: 0 }}>
            <Tabs 
              value={statusTab} 
              onChange={(_, v) => setStatusTab(v)} 
              aria-label="kzm status tabs"
              sx={{ 
                minHeight: 40,
                height: 40,
                flexGrow: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: '2px' }
              }}
            >
            <Tab 
              disableRipple
              label={`Wszystkie (${tabCounts.all})`} 
              value="Wszystkie"
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
            <Tab 
              disableRipple
              label={`Zgłoszone (${tabCounts.zgłoszone})`} 
              value="Zgłoszone"
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
            <Tab 
              disableRipple
              label={`Zaakceptowane (${tabCounts.zaakceptowane})`} 
              value="Zaakceptowane"
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
            <Tab 
              disableRipple
              label={`Uzupełnić (${tabCounts.uzupełnić})`} 
              value="Uzupełnić"
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
            <Tab 
              disableRipple
              label={`Odrzucone (${tabCounts.odrzucone})`} 
              value="Odrzucone"
              sx={{
                textTransform: 'none',
                color: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                px: 3,
                minHeight: 32,
                height: 32,
                mt: '8px',
                bgcolor: '#F1F1F6',
                borderRadius: '4px 4px 0 0',
                '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
              }}
            />
          </Tabs>
        </Box>
        </Box>

        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Pasek akcji – nad tabelą, wyrównany do prawej */}
          <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
            {/* Grupa akcji */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Ustawienia KZM">
                <span>
                  <IconButton onClick={()=>{ setSettingsOpen(true); }}>
                    <Box component="img" src="/assets/icons/settings.svg" alt="Ustawienia" sx={{ width: 22, height: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Dodaj KZM">
                <span>
                  <IconButton onClick={()=>{ setAddOpen(true); }} data-hotkey-add="true">
                    <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {/* Odstęp 16px między grupami ikon */}
            <Box sx={{ width: 16 }} />
            {/* Dostosuj kolumny – ostatnia ikona, na prawej krawędzi */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '4px',
                  p: 0,
                  alignSelf: 'flex-end',
                  bgcolor: 'transparent',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                }}
                onClick={() => { 
                  // TODO: Implementacja dostosowywania kolumn
                  console.log('Dostosuj kolumny');
                }}
              >
                <Box
                  component="img"
                  alt="kolumny"
                  src={'/assets/icons/frame-344.svg?v=2'}
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              mt: 0,
              height: 'auto',
              overflow: 'auto',
              position: 'relative',
              border: '1px solid #d0d0d6',
              borderBottom: '1px solid #d0d0d6',
              borderRadius: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 4,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              overflowX: 'hidden',
              overflowY: 'auto',
              boxShadow: 'none',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { width: 0, height: 0 }
            }}
          >
            <Table 
              size="small" 
              stickyHeader 
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
                '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
                '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }}></TableCell>
                  <TableCell sx={{ width: 160 }}>ID</TableCell>
                  <TableCell sx={{ width: 110 }}>Przygotowano</TableCell>
                  <TableCell>Opis</TableCell>
                  <TableCell sx={{ width: 120, textAlign:'right' }}>Status Karty</TableCell>
                  <TableCell sx={{ width: 120 }}>Data statusu</TableCell>
                  <TableCell sx={{ width: 44, textAlign:'center' }}></TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: 'nowrap' }}>Zatwierdzono</TableCell>
                  <TableCell sx={{ width: 100, textAlign: 'center' }}></TableCell>
                  <TableCell sx={{ width: 44, textAlign: 'right' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ p: 0.5 }}>
                      {(r.prepared_by_avatar_url || r.prepared_by_name) ? (
                        <Tooltip title={r.prepared_by_name || ''} arrow>
                          <Avatar src={r.prepared_by_avatar_url || undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                            {(r.prepared_by_name || '').substring(0,1)}
                          </Avatar>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell title={r.kzm_id || String(r.id)}>{r.kzm_id || r.id}</TableCell>
                    <TableCell>{String(r.prepared_at || '').slice(0,10)}</TableCell>
                    <TableCell sx={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <Tooltip title={`${r.project_id} ${r.project_name || ''}`} arrow>
                        <span>{r.project_id} {r.project_name || ''}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{cardStatusChip(r.card_status || r.status)}</TableCell>
                    <TableCell>{r.card_status_at ? String(r.card_status_at).slice(0,10) : (r.status_at ? String(r.status_at).slice(0,10) : '')}</TableCell>
                    <TableCell align="center">{approvalStatusIcon(r.approval_status)}</TableCell>
                    <TableCell>{r.approval_status_at ? String(r.approval_status_at).slice(0,10) : ''}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap: 0.5 }}>
                        {r.google_drive_file_url ? (
                          <>
                            <Tooltip title="Otwórz w Google Drive" arrow>
                              <IconButton size="small" onClick={()=>{ try { window.open(String(r.google_drive_file_url), '_blank'); } catch {} }}>
                                <img src={driveSvg} alt="Drive" style={{ width: 16, height: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Skopiuj link" arrow>
                              <IconButton size="small" onClick={()=>{ try { navigator.clipboard.writeText(String(r.google_drive_file_url)); } catch {} }}>
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                        <Tooltip title="Podgląd" arrow>
                          <IconButton size="small" onClick={()=>setPreview(r)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0.5 }}>
                      <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); setMenuRow(r); setMenuPosition({ top: e.clientY, left: e.clientX }); }}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {renderMenu()}

          {/* Lokalny dialog wysyłki KZM */}
          <Dialog open={sendOpen} onClose={()=>setSendOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 2 }}>
                <span>Wyślij KZM {menuRow ? (menuRow.kzm_id || menuRow.id) : ''}</span>
                {sendBranch ? (<Chip label={sendBranch} size="small" color={branchChipColor(sendBranch)} />) : null}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {sendLoading && (<Box sx={{ mb: 2 }}><LinearProgress /></Box>)}
              <Box sx={{ display:'grid', gap: 2 }}>
                <TextField select label="OD" size="small" value={sendFrom} onChange={(e)=>setSendFrom(String(e.target.value))}>
                  <MenuItemMUI value="powiadomienia@unitresco.com">powiadomienia@unitresco.com</MenuItemMUI>
                  <MenuItemMUI value={(user as any)?.email || (user as any)?.login || ''}>{(user as any)?.email || (user as any)?.login || 'mój e‑mail'}</MenuItemMUI>
                </TextField>
                <TextField label="DO" placeholder="adresy rozdzielane przecinkami" fullWidth size="small" value={sendTo} onChange={(e)=>setSendTo(e.target.value)} />
                <TextField label="DW" placeholder="DW (opcjonalnie)" fullWidth size="small" value={sendCc} onChange={(e)=>setSendCc(e.target.value)} />
                <TextField label="TEMAT" fullWidth size="small" value={sendSubject} onChange={(e)=>setSendSubject(e.target.value)} />
                <TextField label="TREŚĆ WIADOMOŚCI" fullWidth multiline minRows={6} value={sendBody} onChange={(e)=>setSendBody(e.target.value)} />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Załączniki</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {sendAttachments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Brak załączników</Typography>
                  ) : (
                    <FormControlLabel control={<Checkbox size="small" checked={sendIncludeAll} onChange={(e)=>setSendIncludeAll(e.target.checked)} />} label={`Załącz wszystkie (${sendAttachmentsSizeMB.toFixed(1)} MB)`} />
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setSendOpen(false)} disabled={sendLoading}>Anuluj</Button>
              <Button variant="contained" onClick={async()=>{
                try {
                  if (!menuRow) return;
                  setSendLoading(true);
                  await sendKzmEmail(menuRow.kzm_id || String(menuRow.id), { from: sendFrom, to: sendTo, cc: sendCc, subject: sendSubject, body: sendBody, includeAllAttachments: sendIncludeAll });
                  setSendOpen(false);
                  showSnack('success','KZM wysłany');
                  // Odśwież listę
                  const list = await listMaterialApprovals(projectId);
                  setRows(list || []);
                } catch (e:any) {
                  showSnack('error', e?.message || 'Błąd wysyłki KZM');
                } finally {
                  setSendLoading(false);
                }
              }} disabled={sendLoading}>Wyślij</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={!!preview} onClose={()=>setPreview(null)} maxWidth="xl" fullWidth>
            <DialogTitle>{preview ? `Podgląd: ${preview.kzm_id || preview.id}` : 'Podgląd'}</DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {preview && (
                <Box sx={{ display:'flex', gap: 2, height: { xs: 'calc(100vh - 180px)', md: 'calc(100vh - 180px)' }, p: 2 }}>
                  <Box sx={{ flex: 7, border: '2px dashed #9e9e9e', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
                    {previewPdfUrl ? (
                      <iframe title="kzm-preview" src={previewPdfUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                    ) : (
                      <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'text.secondary' }}>Brak podglądu PDF</Box>
                    )}
                  </Box>
                  <Box sx={{ flex: 3, display:'grid', gridTemplateColumns:'160px 1fr', rowGap: 1, columnGap: 2, pr: 1 }}>
                    <Typography variant="body2" color="text.secondary">Projekt</Typography>
                    <Typography variant="body2">{preview.project_id} {preview.project_name || ''}</Typography>
                    <Typography variant="body2" color="text.secondary">Branża</Typography>
                    <Typography variant="body2">{preview.branch}</Typography>
                    <Typography variant="body2" color="text.secondary">Przygotowano</Typography>
                    <Typography variant="body2">{String(preview.prepared_at).slice(0,10)}</Typography>
                    <Typography variant="body2" color="text.secondary">Wysłano</Typography>
                    <Typography variant="body2">{preview.sent_at ? String(preview.sent_at).slice(0,10) : '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">Status Karty</Typography>
                    <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>{cardStatusChip(preview.card_status || preview.status)}<Typography variant="body2">{preview.card_status || preview.status || '—'}</Typography></Box>
                    <Typography variant="body2" color="text.secondary">Data Statusu Karty</Typography>
                    <Typography variant="body2">{preview.card_status_at ? String(preview.card_status_at).slice(0,10) : (preview.status_at ? String(preview.status_at).slice(0,10) : '—')}</Typography>
                    <Typography variant="body2" color="text.secondary">Status zatwierdzenia</Typography>
                    <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>{approvalStatusIcon(preview.approval_status)}<Typography variant="body2">{preview.approval_status || '—'}</Typography></Box>
                    <Typography variant="body2" color="text.secondary">Data statusu zatwierdzenia</Typography>
                    <Typography variant="body2">{preview.approval_status_at ? String(preview.approval_status_at).slice(0,10) : '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">Opis</Typography>
                    <Typography variant="body2">{preview.material_name}</Typography>
                    <Typography variant="body2" color="text.secondary">Przygotował</Typography>
                    <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                      {preview.prepared_by_avatar_url ? (<Avatar src={preview.prepared_by_avatar_url} sx={{ width: 25, height: 25 }} />) : null}
                      <Typography variant="body2">{preview.prepared_by_name || '—'}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setPreview(null)}>Zamknij</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog dodawania/edycji – z zablokowanym projektem */}
          <Dialog open={addOpen} onClose={()=>setAddOpen(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { width: '96vw', maxWidth: '96vw' } }}>
            <DialogTitle>{menuRow ? 'Edytuj kartę materiałową (KZM)' : 'Dodaj nową kartę materiałową (KZM)'}</DialogTitle>
            <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
              {saving && (
                <Box sx={{ px: 2, pt: 1 }}>
                  <LinearProgress color="primary" />
                </Box>
              )}
              <Box sx={{ display:'grid', gridTemplateColumns:'6fr 4fr', gap: 0, height: '100%' }}>
                <Box sx={{ p: 2, overflowY: 'auto', height: '100%' }}>
                  <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, height: '68vh', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', backgroundColor: 'background.paper' }}>
                    {activeAttachment ? (
                      activeAttachment?.driveId ? (
                        <iframe
                          key={activeAttachmentId || undefined}
                          src={`${API_BASE_URL}/kzms/attachments/${encodeURIComponent(activeAttachment.driveId)}/content`}
                          style={{ width: '100%', height: '100%', border: 0 }}
                          title={activeAttachment.name || 'Podgląd PDF'}
                        />
                      ) : (
                        <object key={activeAttachmentId || undefined} data={activeAttachment.url} type="application/pdf" width="100%" height="100%">
                          <Box sx={{ textAlign:'center', color:'text.secondary' }}>
                            <PictureAsPdf sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="body2">Podgląd PDF niedostępny</Typography>
                          </Box>
                        </object>
                      )
                    ) : (
                      <Box sx={{ textAlign:'center', color:'text.secondary' }}>
                        <UploadFile sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body1">Przeciągnij i upuść plik PDF tutaj</Typography>
                        <Typography variant="body2" sx={{ my: 1 }}>lub</Typography>
                        <Button variant="outlined" onClick={()=>fileInputRef.current?.click()}>Wybierz plik PDF</Button>
                      </Box>
                    )}
                    <input ref={fileInputRef} type="file" accept="application/pdf" multiple style={{ display:'none' }} onChange={(e)=>{ const files = Array.from(e.currentTarget.files || []); e.currentTarget.value=''; if (files.length) handleAddFiles(files as File[]); }} />
                  </Box>
                </Box>

                <Box sx={{ p: 2, borderLeft: '1px solid', borderColor: 'divider', backgroundColor: (t)=>t.palette.mode==='light'?'#fafafa':'rgba(255,255,255,0.04)', overflowY: 'auto', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color:'text.secondary' }}>Dane podstawowe</Typography>
                  <TextField size="small" fullWidth sx={{ mb: 1.5 }} label="Projekt" value={`${projectId} ${projectData?.project_name || ''}`.trim()} disabled />
                  <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                    <Select value={addBranch} displayEmpty onChange={(e)=>setAddBranch((e.target.value || '') as any)} renderValue={(v)=> v || 'Branża'}>
                      <MenuItemMUI value=""><em>Wybierz</em></MenuItemMUI>
                      {branches.map(b => (<MenuItemMUI key={b} value={b}>{b}</MenuItemMUI>))}
                    </Select>
                  </FormControl>
                  <TextField label="Nazwa handlowa materiału" value={addMaterialName} onChange={(e)=>setAddMaterialName(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }} />
                  <TextField label="Miejsce wbudowania" value={addInstallPlace} onChange={(e)=>setAddInstallPlace(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }} />
                  <TextField label="Data zgłoszenia" type="date" value={addSubmissionDate} onChange={(e)=>setAddSubmissionDate(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }} required InputLabelProps={{ shrink: true }} />
                  <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                    <Select value={addCompliance} displayEmpty onChange={(e)=>setAddCompliance((e.target.value || '') as any)} renderValue={(v)=> v || 'Zgodność z projektem'}>
                      <MenuItemMUI value=""><em>Wybierz</em></MenuItemMUI>
                      <MenuItemMUI value="Zgodny z projektem">Zgodny z projektem</MenuItemMUI>
                      <MenuItemMUI value="Równoważny">Równoważny</MenuItemMUI>
                      <MenuItemMUI value="Nie określony w projekcie">Nie określony w projekcie</MenuItemMUI>
                    </Select>
                  </FormControl>

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color:'text.secondary' }}>Załączniki</Typography>
                  <Button size="small" variant="outlined" startIcon={<Add />} onClick={()=>fileInputRef.current?.click()} sx={{ mb: 1 }}>Dodaj z dysku</Button>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', display:'grid', gap: 1 }}>
                    {attachments.length === 0 && (
                      <Typography variant="body2" color="text.secondary">Brak załączników</Typography>
                    )}
                    {attachments.map((a, i) => (
                      <Box key={a.id} onClick={()=>setActiveAttachmentId(a.id)} sx={{ display:'grid', gridTemplateColumns:'auto 1fr auto auto', gap: 1, alignItems:'center', p: 1, border: '1px solid', borderColor: a.id===activeAttachmentId ? 'primary.main' : 'divider', borderRadius: 1, bgcolor: a.id===activeAttachmentId ? 'action.selected' : 'background.paper', cursor:'pointer', '&:hover':{ bgcolor: a.id===activeAttachmentId ? 'action.selected' : 'action.hover' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 34 }}>{`Z${i+1}:`}</Typography>
                        <TextField size="small" placeholder="Nazwa załącznika" value={a.name} onClick={(e)=>e.stopPropagation()} onChange={(e)=> setAttachments(prev => prev.map(x => x.id === a.id ? { ...x, name: e.target.value } : x))} />
                        <IconButton size="small" onClick={()=>setActiveAttachmentId(a.id)} title="Pokaż w podglądzie">
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={(e)=>{ e.stopPropagation(); setAttachments(prev => { const next = prev.filter(x => x.id !== a.id); if (a.id === activeAttachmentId) { setActiveAttachmentId(next[0]?.id || null); } return next; }); }} title="Usuń">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent:'space-between', alignItems:'center', px: 2 }}>
              <Box />
              <Box>
                {menuRow && (
                  <Button color="error" onClick={async()=>{
                    if (!menuRow) return;
                    const confirm = window.confirm(`Usunąć KZM ${menuRow.kzm_id || menuRow.id}?\nUwaga: folder na Google Drive należy usunąć ręcznie (brak uprawnień aplikacji).`);
                    if (!confirm) return;
                    try {
                      setSaving(true);
                      await deleteKzm(menuRow.kzm_id || String(menuRow.id));
                      setAddOpen(false);
                      const list = await listMaterialApprovals(projectId);
                      setRows(list || []);
                      showSnack('success', 'KZM usunięty (folder na Drive usuń ręcznie)');
                    } catch (e) {
                      showSnack('error', 'Błąd usuwania KZM');
                    } finally {
                      setSaving(false);
                    }
                  }}>Usuń</Button>
                )}
                <Button onClick={()=>setAddOpen(false)} disabled={saving} sx={{ ml: 1 }}>Anuluj</Button>
                <Button variant="contained" disabled={saving} sx={{ ml: 1 }} onClick={async()=>{
                  try {
                    setSaving(true);
                    if (!projectId) { showSnack('error','Brak ID projektu'); return; }
                    if (!addBranch) { showSnack('error','Wybierz branżę'); return; }
                    if (!addMaterialName.trim()) { showSnack('error','Wpisz nazwę handlową materiału'); return; }
                    if (!addInstallPlace.trim()) { showSnack('error','Wpisz miejsce wbudowania'); return; }
                    if (!addSubmissionDate) { showSnack('error','Wybierz datę zgłoszenia'); return; }
                    if (!addCompliance) { showSnack('error','Wybierz zgodność z projektem'); return; }
                    if (menuRow) {
                      const keepDriveIds = attachments.filter((a:any)=>!!a.driveId).map((a:any)=>({ driveId: a.driveId as string, name: a.name as string, description: a.name as string }));
                      const newFiles = attachments.filter((a:any)=>!a.driveId && a.fileObject).map((a:any)=>({ file: a.fileObject as File, name: a.name as string, description: a.name as string }));
                      await reviseKzm(menuRow.kzm_id || String(menuRow.id), { material_trade_name: addMaterialName, install_place: addInstallPlace, submission_date: addSubmissionDate, compliance: addCompliance as any, status: 'Przygotowany' }, { keepDriveIds, newFiles, googleAccessToken: (user as any)?.google_access_token });
                      setAddOpen(false);
                      const list = await listMaterialApprovals(projectId);
                      setRows(list || []);
                      showSnack('success', 'Zapisano rewizję');
                    } else {
                      const files = attachments.map((a:any)=>a.fileObject).filter(Boolean) as File[];
                      const fileDescriptions = attachments.filter((a:any)=>a.fileObject).map((a:any)=>a.name || '');
                      await quickCreateKzmUpload({ project_id: projectId, material_branch: addBranch as string, files, material_trade_name: addMaterialName, install_place: addInstallPlace, submission_date: addSubmissionDate, compliance: addCompliance || undefined }, (user as any)?.google_access_token, fileDescriptions);
                      setAddOpen(false);
                      const list = await listMaterialApprovals(projectId);
                      setRows(list || []);
                      showSnack('success', 'KZM zapisany');
                    }
                  } catch (err) {
                    console.error('Błąd zapisu KZM', err);
                    showSnack('error', 'Błąd zapisu KZM');
                  } finally {
                    setSaving(false);
                  }
                }}>{saving ? (menuRow ? 'Zapisywanie rewizji…' : 'Zapisywanie…') : (menuRow ? 'Zapisz rewizję' : 'Zapisz') }</Button>
              </Box>
            </DialogActions>
          </Dialog>

          <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ mt: 7 }}>
            <Alert elevation={3} onClose={()=>setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
              {snack.msg}
            </Alert>
          </Snackbar>

          {/* Dialog ustawień KZM */}
          <Dialog open={settingsOpen} onClose={()=>setSettingsOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Ustawienia KZM</DialogTitle>
            <DialogContent dividers>
              <KzmSettings projectId={projectId} initialJson={(projectData as any)?.kzm_emails || ''} />
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setSettingsOpen(false)}>Zamknij</Button>
            </DialogActions>
          </Dialog>
        </Box>
    </Box>
  );
};

export default KzmTab;
